import React, {Component} from 'react';
import {createPortal, findDOMNode} from 'react-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';
import i18n from '../../../utils/i18n';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import {
  default as TextInput,
  InputUITypes,
  ValidationTypes
} from '@adsk/bim360-matrix-react-text-input';
import {issueAttributeListItemValidators} from '../../../utils/issueAttributes-helpers';

class IssueAttributesAdminDraggableListItem extends Component {
  constructor(props) {
    super(props);

    // eslint-disable-next-line react/no-find-dom-node
    this.portal = findDOMNode(props.container);
    this.onAnswerEditEnd = this.onAnswerEditEnd.bind(this);
    this.onAnswerChange = this.onAnswerChange.bind(this);
    this.onAnswerRemove = this.onAnswerRemove.bind(this);
  }

  onAnswerEditEnd(value, isCancel, initialValue, inlineEditFinisher, isValid) {
    this.props.onAnswerEditEnd(this.props.index, value, isCancel, initialValue, inlineEditFinisher, isValid);
  }

  onAnswerChange(value) {
    this.props.onAnswerChange(this.props.index, this.props.answer, value);
  }

  onAnswerRemove() {
    this.props.onAnswerRemove(this.props.index);
  }

  getValidationProps() {
    const options = [...this.props.options];
    options.splice(this.props.index, 1);
    const values = options.map(option => option.value.trim().toLowerCase());
    return {
      type: ValidationTypes.POPOVER,
      ignore: this.props.answer.isNew || this.props.answer.valid,
      validator: issueAttributeListItemValidators(values),
      typeProps: {
        container: this.props.container
      }
    };
  }

  getInputProps() {
    return {
      autofocus: this.props.answer.isNew,
      className: 'IssueAttributesAdminDraggableListItem__text',
      'data-testid': 'IssueAttributesAdminDraggableListItem__text',
      onInlineEditEnd: this.onAnswerEditEnd
    };
  }

  renderItemAnswer() {
    return (
      <TextInput
        value={this.props.answer.value}
        validationProps={this.getValidationProps()}
        inputProps={this.getInputProps()}
        placeholder={i18n.t('issueAttributesAdmin.modal.newAnswer')}
        inputUIType={InputUITypes.INLINE_EDIT}
        onChange={this.onAnswerChange}
      />
    );
  }

  _renderListItem() {
    const {provided, snapshot, isDragDisabled} = this.props;
    const isDragging = snapshot.isDragging;
    const itemCls = cx('IssueAttributesAdminDraggableListItem', {
      'IssueAttributesAdminDraggableListItem--dragging': isDragging,
      'IssueAttributesAdminDraggableListItem--drag-disabled': isDragDisabled
    });
    return (
      <div
        data-testid="IssueAttributesAdminDraggableListItem"
        className={itemCls}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.droppableProps}
        tabIndex="-1"
      >
        <div className="IssueAttributesAdminDraggableListItem__answer">
          <div
            className="IssueAttributesAdminDraggableListItem__drag-handle"
            data-testid="IssueAttributesAdminDraggableListItem__drag-handle"
            {...provided.dragHandleProps}
            style={{visibility: this.props.options.length <= 1 ? 'hidden' : ''}}
          >
            <SvgIcon
              svgId="drag"
              transform="rotate(90) translate(0, -16)"
              className="IssueAttributesAdminDraggableListItem__drag-handle-icon"
            />
          </div>
          {this.renderItemAnswer()}
        </div>
        {this.props.options.length > 1 && (
          <IconButton
            iconHeight="16px"
            iconWidth="14px"
            svgId="trash"
            tabIndex="-1"
            className="IssueAttributesAdminDraggableListItem__delete"
            data-testid="IssueAttributesAdminDraggableListItem__delete"
            onClick={this.onAnswerRemove}
          />
        )}
      </div>
    );
  }

  render() {
    const {snapshot} = this.props;
    const usePortal = snapshot.isDragging;
    const draggableListItem = this._renderListItem();

    return usePortal ? createPortal(draggableListItem, this.portal) : draggableListItem;
  }
}

IssueAttributesAdminDraggableListItem.displayName = 'IssueAttributesAdminDraggableListItem';

IssueAttributesAdminDraggableListItem.propTypes = {
  container: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  answer: PropTypes.object.isRequired,
  provided: PropTypes.object.isRequired,
  snapshot: PropTypes.object.isRequired,
  isDragDisabled: PropTypes.bool.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAnswerEditEnd: PropTypes.func.isRequired,
  onAnswerRemove: PropTypes.func.isRequired,
  onAnswerChange: PropTypes.func.isRequired
};

export default IssueAttributesAdminDraggableListItem;
